import axios from "axios";
import { SolutionProject, Problem, UserSolution, SolutionTask } from "./types";
import { RecordedEvent } from "./utils/event-recorder";

// Use full URL in development (when running npm start), relative URL in production (Docker with nginx)
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? "https://api.habitat.inc/v1" : "/api/v1");

const getAuthHeaders = () => {
  const accessCode = localStorage.getItem("accessCode");
  return accessCode ? { Authorization: `Bearer ${accessCode}` } : {};
};


// PlaygroundPreview type for PlaygroundModal
export interface PlaygroundPreview {
  id: string;
  status: 'creating' | 'ready' | 'failed';
  access_url?: string;
  playground_id?: string;
  software: string;
  user_id: string;
  message?: string;
  error?: string;
  started_at?: string;
  state_name?: string;
  state_type?: 'input' | 'output';
  resource_type?: string;
  resource_id?: string;
  ready_at?: string;
  last_accessed_at?: string;
}

// Playground API functions for PlaygroundModal
export const getPlaygroundPreviewStatus = async (software: string, problemId: string): Promise<PlaygroundPreview> => {
  const supportedSoftware = ['erpnext', 'immich', 'odoo'];
  if (!supportedSoftware.includes(software.toLowerCase())) {
    throw new Error(`Playground with state is not supported for ${software}`);
  }

  const response = await axios.post(`${API_BASE_URL}/playgrounds/previews/problem`, { 
    problem_id: problemId,
    state_name: 'start_state.zip',  // Load START state for solution recording
    state_type: 'input',
    software 
  }, { headers: getAuthHeaders() });
  return response.data;
};

export const checkPlaygroundPreviewHealth = async (previewId: string): Promise<{
  preview_id: string;
  software: string;
  status: 'healthy' | 'unhealthy' | 'failed';
  status_code?: number;
  response_time_ms?: number;
  error?: string;
}> => {
  const response = await axios.get(`${API_BASE_URL}/playgrounds/previews/${previewId}/health`, 
    { headers: getAuthHeaders() });
  return response.data;
};

export const deletePlaygroundPreview = async (previewId: string): Promise<{ message: string }> => {
  const response = await axios.delete(`${API_BASE_URL}/playgrounds/previews/${previewId}`, 
    { headers: getAuthHeaders() });
  return response.data;
};

// Playground recording API functions
export const startPlaygroundRecording = async (previewId: string): Promise<{ recording_id: string; status: string; message: string }> => {
  const response = await axios.post(
    `${API_BASE_URL}/playgrounds/previews/${previewId}/start-record`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const stopPlaygroundRecording = async (previewId: string, videoBlob?: Blob, events?: RecordedEvent[]): Promise<void> => {
  const formData = new FormData();
  if (videoBlob) {
    formData.append('video_file', videoBlob, 'recording.webm');
  }
  if (events) {
    formData.append('events', JSON.stringify(events));
  }
  await axios.post(
    `${API_BASE_URL}/playgrounds/previews/${previewId}/end-record`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

export const submitPlaygroundSolution = async (data: {
  problem_id: string;
  solution_project_id?: string;
  playground_session_id: string;
  recording_id?: string;
  submission_notes?: string;
  final_state?: any;
}): Promise<UserSolution> => {
  // Include recording_id in the final_state so backend can retrieve the exported database
  const payload = {
    problem_id: data.problem_id,
    solution_project_id: data.solution_project_id,
    playground_session_id: data.playground_session_id,
    submission_notes: data.submission_notes,
    final_state: data.recording_id ? {
      recording_id: data.recording_id,
      // This tells the backend where to find the exported state from end_recording
      state_export_path: `gs://habitat-contractor-tools-prod/recordings/${data.recording_id}/end_state.zip`
    } : data.final_state
  };
  
  const response = await axios.post(
    `${API_BASE_URL}/solutions/submissions/web`,
    payload,
    { headers: getAuthHeaders() }
  );
  return response.data.solution;
};

// Solution Project API calls
export const fetchUserSolutionProjects = async (): Promise<SolutionProject[]> => {
  const userId = localStorage.getItem("userId");
  if (!userId) throw new Error("User ID not found");

  const response = await axios.get(
    `${API_BASE_URL}/solutions/projects/user/${userId}`,
    { headers: getAuthHeaders() }
  );
  return response.data.solution_projects;
};

export const fetchProjectProblems = async (projectId: string): Promise<Problem[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/solutions/projects/${projectId}/problems`,
    { headers: getAuthHeaders() }
  );
  return response.data.problems;
};

export const fetchUserSolutions = async (
  userId: string,
  projectId?: string
): Promise<UserSolution[]> => {
  const params = new URLSearchParams();
  if (projectId) {
    params.append("solution_project_id", projectId);
  }
  
  const response = await axios.get(
    `${API_BASE_URL}/solutions/submissions/user/${userId}?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data.solutions;
};

// Problem Run API calls  
export interface ProblemRun {
  id: string;
  problem_id: string;
  status: string;
  score?: number;
  final_score?: number;
  created_at: string;
}

export const getProblemRuns = async (problemId: string): Promise<{ items: ProblemRun[] }> => {
  const response = await axios.get(
    `${API_BASE_URL}/problems/${problemId}/runs`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Score Dispute API calls
export interface ScoreDispute {
  id: string;
  problem_run_id: string;
  user_id: string;
  note: string;
  proposed_score: string;
  status: string;
  reason: string;
  created_at: string;
  updated_at: string;
}

export const createScoreDispute = async (problemRunId: string, reason?: string): Promise<ScoreDispute> => {
  const note = reason 
    ? `Marked as Bad Task from Solution Platform for reason: ${reason}`
    : "Marked as 'Bad Task' in the Solution Platform";
  
  const response = await axios.post(
    `${API_BASE_URL}/score-disputes`,
    {
      problem_run_id: problemRunId,
      note: note,
      proposed_score: "bad_task"
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteScoreDispute = async (disputeId: string): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/score-disputes/${disputeId}`,
    { headers: getAuthHeaders() }
  );
};

export const getScoreDisputesForProblemRun = async (problemRunId: string): Promise<ScoreDispute[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/problem-runs/${problemRunId}/score-disputes`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Cache for dispute checks to avoid repeated API calls
const disputeCheckCache = new Map<string, { result: boolean; disputeId?: string; reason?: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const checkProblemHasBadTaskDispute = async (problemId: string): Promise<{ hasDispute: boolean; disputeId?: string; reason?: string }> => {
  // Check cache first
  const cached = disputeCheckCache.get(problemId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return { hasDispute: cached.result, disputeId: cached.disputeId, reason: cached.reason };
  }

  try {
    // Get problem runs for this problem
    const runsResponse = await getProblemRuns(problemId);
    if (!runsResponse.items || runsResponse.items.length === 0) {
      disputeCheckCache.set(problemId, { result: false, timestamp: Date.now() });
      return { hasDispute: false };
    }
    
    // Check only the most recent problem run for disputes (most likely to have disputes)
    const mostRecentRun = runsResponse.items[runsResponse.items.length - 1];
    const disputes = await getScoreDisputesForProblemRun(mostRecentRun.id);
    const badTaskDispute = disputes.find(
      d => (d.note === "Marked as 'Bad Task' in the Solution Platform" || 
            d.note.startsWith("Marked as Bad Task from Solution Platform for reason:")) && 
           d.proposed_score === "bad_task"
    );
    
    // Extract reason from the note if present
    let reason: string | undefined;
    if (badTaskDispute?.note.startsWith("Marked as Bad Task from Solution Platform for reason:")) {
      reason = badTaskDispute.note.replace("Marked as Bad Task from Solution Platform for reason:", "").trim();
    }
    
    // Cache the result
    disputeCheckCache.set(problemId, { 
      result: !!badTaskDispute, 
      disputeId: badTaskDispute?.id,
      reason: reason,
      timestamp: Date.now() 
    });
    return { hasDispute: !!badTaskDispute, disputeId: badTaskDispute?.id, reason };
  } catch (error) {
    console.error("Error checking for bad task dispute:", error);
    // Don't cache errors
    return { hasDispute: false };
  }
};

// Clear a specific problem from cache when a new dispute is created
export const clearDisputeCache = (problemId: string) => {
  disputeCheckCache.delete(problemId);
};

export const submitDesktopSolution = async (data: {
  problem_id: string;
  solution_project_id?: string;
  solution_files: File[];
  submission_notes?: string;
}): Promise<UserSolution> => {
  // Generate a unique submission ID
  const submission_id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // If no files (impossible task), submit directly
  if (data.solution_files.length === 0) {
    const response = await axios.post(
      `${API_BASE_URL}/solutions/submissions/desktop`,
      {
        problem_id: data.problem_id,
        submission_id,
        file_paths: [],
        solution_project_id: data.solution_project_id,
        submission_notes: data.submission_notes,
      },
      { headers: getAuthHeaders() }
    );
    return response.data.solution;
  }

  // 1. Get filenames
  const filenames = data.solution_files.map(f => f.name);

  // 2. Generate upload URLs
  const urlResponse = await axios.post(
    `${API_BASE_URL}/solutions/submissions/upload-urls`,
    {
      submission_id,
      filenames,
      expiration_minutes: 15,
    },
    { headers: getAuthHeaders() }
  );
  const uploadUrls = urlResponse.data.urls;

  // 3. Upload files to GCS using signed URLs
  await Promise.all(data.solution_files.map(async (file, index) => {
    const url = uploadUrls[index];
    // Only upload if it's an HTTP URL (not a local path)
    if (url.startsWith('http')) {
      await axios.put(url, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });
    }
  }));

  // 4. Build file paths that were uploaded
  const file_paths = filenames.map(filename =>
    `gs://${process.env.REACT_APP_GCS_BUCKET || 'habitat-contractor-tools-prod'}/state-uploads/${submission_id}/${filename}`
  );

  // 5. Submit the solution with file paths
  const response = await axios.post(
    `${API_BASE_URL}/solutions/submissions/desktop`,
    {
      problem_id: data.problem_id,
      submission_id,
      file_paths,
      solution_project_id: data.solution_project_id,
      submission_notes: data.submission_notes,
    },
    { headers: getAuthHeaders() }
  );
  return response.data.solution;
};

export const submitWebSolution = async (data: {
  problem_id: string;
  solution_project_id?: string;
  playground_session_id: string;
  submission_notes?: string;
  final_state?: any;
}): Promise<UserSolution> => {
  const response = await axios.post(
    `${API_BASE_URL}/solutions/submissions/web`,
    data,
    { headers: getAuthHeaders() }
  );
  return response.data.solution;
};

// Fetch problem run by problem ID
export const fetchProblemRun = async (problemId: string): Promise<any> => {
  const response = await axios.get(
    `${API_BASE_URL}/problems/${problemId}/runs`,
    { headers: getAuthHeaders() }
  );
  // Get the first (most recent) run - response structure matches label-platform
  const runs = response.data.items || response.data;
  return runs?.[0];
};


// Download input file for desktop applications
export const downloadInputFile = async (
  problemId: string,
): Promise<void> => {
  try {
    // First, get problem run for this problem
    const problemRun = await fetchProblemRun(problemId);
    if (!problemRun || !problemRun.id) {
      alert("No problem run available for downloading input file.");
      return;
    }

    // Get problem details to find the input filename
    const problemResponse = await axios.get(
      `${API_BASE_URL}/problems/${problemId}`,
      { headers: getAuthHeaders() }
    );
    
    const problem = problemResponse.data;
    const inputFile = problem.spec?.input_file;
    
    if (!inputFile) {
      alert("No input file available for this problem.");
      return;
    }
    
    // Download the input file using the inputs endpoint
    const response = await axios.get(
      `${API_BASE_URL}/problem-runs/${problemRun.id}/inputs/${inputFile}`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = inputFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    alert(
      "Failed to download input file. The file may not be available for this problem.",
    );
  }
};

// Delete a solution submission
export const deleteSolution = async (solutionId: string): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/solutions/submissions/${solutionId}`,
    { headers: getAuthHeaders() }
  );
};

// Helper function to get project tasks with solution status - optimized version
export const getProjectTasks = async (projectId: string): Promise<SolutionTask[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/solutions/projects/${projectId}/tasks`,
    { headers: getAuthHeaders() }
  );
  
  // Transform the response to match SolutionTask interface
  return response.data.tasks.map((task: any) => ({
    problem: task.problem,
    solution: task.solution,
    solutions: task.solutions || [],  // Include all solutions
    is_completed: task.is_completed,
    has_dispute: task.has_dispute,
    dispute: task.dispute,
  }));
};